import InfiniteScroll from 'react-infinite-scroll-component';
import { useState } from 'react';
import { XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Drawer, DrawerContent, DrawerHeader } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Title } from '@/components/ui/title';
import PostCard from '@/components/Post/PostCard';
import useGlobalSearch from './hooks/useGlobalSearch';
import useGlobalSearchSuggestions from './hooks/useGlobalSearchSuggestions';
import { Skeleton } from '@/components/ui/skeleton';
import SearchBar from './components/SearchBar';
import AutoCompleteResults from './components/AutocompleteResults';
import { cn } from '@/lib/utils';

export default function GlobalSearch({ className }: { className?: string }) {
  const [page, setPage] = useState<number>(1);
  const {
    query,
    setQuery,
    searchResults,
    isSearchDrawerOpen,
    setIsSearchDrawerOpen,
    handleSearch,
  } = useGlobalSearch({ page });

  const {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    suggestionsRef,
    inputRef,
  } = useGlobalSearchSuggestions(query);
  const navigate = useNavigate();

  const handleSuggestionClick = (id: string) => {
    navigate(`/model/${id}`);
    setShowSuggestions(false);
    setQuery('');
  };

  const fetchMoreData = () => {
    setPage((prev) => prev + 1);
  };

  const handleCloseSearchDrawer = () => {
    setIsSearchDrawerOpen(false);
    setQuery('');
  };

  return (
    <div className={cn('relative w-full max-w-[400px]', className)}>
      <SearchBar
        ref={inputRef}
        query={query}
        setQuery={setQuery}
        handleSearch={handleSearch}
        setShowSuggestions={setShowSuggestions}
      />

      <AutoCompleteResults
        ref={suggestionsRef}
        suggestions={suggestions}
        showSuggestions={showSuggestions}
        handleSuggestionClick={handleSuggestionClick}
      />

      <Drawer open={isSearchDrawerOpen} onOpenChange={setIsSearchDrawerOpen}>
        <DrawerContent className="md:px-12 pb-12 h-full">
          <DrawerHeader className="flex items-center justify-between">
            <Title title={`Search Results (${searchResults.length})`} />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCloseSearchDrawer}
            >
              <XIcon className="h-5 w-5" />
            </Button>
          </DrawerHeader>
          <div className="h-full overflow-y-auto">
            <InfiniteScroll
              dataLength={searchResults.length}
              next={fetchMoreData}
              hasMore={(searchResults?.length ?? 0) === 20}
              loader={
                <>
                  {Array.from({ length: 20 }).map((_, index) => (
                    <Skeleton key={index} className="w-[342px] h-[478px]" />
                  ))}
                </>
              }
              className="flex flex-wrap sm:justify-start justify-center py-2 w-full gap-10 items-center overflow-y-scroll"
            >
              {searchResults.map((post) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div onClick={handleCloseSearchDrawer} key={post._id}>
                  <PostCard
                    id={post._id}
                    authorName={`${post?.user?.firstname} ${post?.user?.lastname}`}
                    authorAvatar={post?.user?.profilePicture}
                    authorUsername={post?.user?.username}
                    title={post.title}
                    description={post.description}
                    likesCount={post.likes}
                    commentsCount={post.comments}
                    attachments={post.attachments}
                    createdAt={post.createdAt}
                    category={post.category}
                    hasLikedByUser={post?.hasLikedByUser}
                    isOnefinityOfficial={post?.user?.isOnefinityOfficial}
                    isOwner={false}
                    partnerData={post?.partner}
                  />
                </div>
              ))}
            </InfiniteScroll>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
